<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">Görünüm</h3>
      <span class="badge badge-ghost badge-sm capitalize">{{ formatThemeName(currentTheme) }}</span>
    </div>

    <!-- Quick light / dark toggle -->
    <div class="flex rounded-lg border border-base-300 p-1 bg-base-200/50">
      <button
        v-for="mode in quickModes"
        :key="mode.value"
        class="flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-all"
        :class="currentTheme === mode.value
          ? 'bg-base-100 text-base-content shadow-sm'
          : 'text-base-content/50 hover:text-base-content'"
        @click="setTheme(mode.value)"
      >
        <span v-html="mode.icon"></span>
        {{ mode.label }}
      </button>
    </div>

    <!-- Popular themes -->
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="theme in popularThemes"
        :key="theme"
        class="btn btn-xs rounded-full capitalize transition-all"
        :class="currentTheme === theme
          ? 'btn-primary'
          : 'btn-ghost border border-base-300 hover:border-primary/40'"
        @click="setTheme(theme)"
      >
        {{ formatThemeName(theme) }}
      </button>
    </div>

    <!-- All themes -->
    <details class="group">
      <summary class="flex cursor-pointer items-center gap-1 text-xs text-base-content/50 hover:text-base-content transition-colors list-none">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-3.5 w-3.5 transition-transform group-open:rotate-90"
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        Tüm temalar ({{ themes.length }})
      </summary>
      <div class="mt-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pt-1">
        <button
          v-for="theme in themes"
          :key="theme"
          class="btn btn-xs btn-ghost rounded-full capitalize border border-transparent hover:border-base-300"
          :class="{ 'btn-primary border-primary': currentTheme === theme }"
          @click="setTheme(theme)"
        >
          {{ formatThemeName(theme) }}
        </button>
      </div>
    </details>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const themes = [
  "light", "dark", "bumblebee", "corporate", "night", "cupcake", "emerald",
  "synthwave", "retro", "cyberpunk", "valentine", "halloween", "garden",
  "forest", "aqua", "lofi", "pastel", "fantasy", "wireframe", "luxury",
  "dracula", "cmyk", "autumn", "business", "acid", "lemonade", "winter",
];

const popularThemes = ["corporate", "cupcake", "emerald", "business", "winter"];

const quickModes = [
  {
    value: 'light',
    label: 'Açık',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>`,
  },
  {
    value: 'dark',
    label: 'Koyu',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>`,
  },
];

const currentTheme = ref('light');

function formatThemeName(theme) {
  return theme.charAt(0).toUpperCase() + theme.slice(1);
}

function setTheme(theme) {
  currentTheme.value = theme;
  localStorage.setItem('theme', theme);
  document.documentElement.setAttribute('data-theme', theme);
  if (theme === 'dark' || theme === 'night') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

onMounted(() => {
  const savedTheme = localStorage.getItem('theme') || 'light';
  setTheme(savedTheme);
});
</script>
